// PublicStorefront — bare/no-auth page rendered at /store/:handle.
// Phase 5.4 Bundle 2 (I1) — redesigned hero + product grid + footer
// affiliate disclosure + noindex when running against sandbox.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ExternalLink, ShoppingBag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_COLORS = {
  emerald: "from-emerald-500 to-teal-700",
  sky:     "from-sky-500     to-indigo-700",
  amber:   "from-amber-500   to-orange-700",
  rose:    "from-rose-500    to-fuchsia-700",
  violet:  "from-violet-500  to-purple-700",
  zinc:    "from-zinc-500    to-zinc-800",
};

function ensureMetaRobots(noindex) {
  // Idempotent: keep one tag named "ap-robots" in <head>.
  const id = "ap-robots";
  let el = document.querySelector(`meta[name="robots"][data-id="${id}"]`);
  if (noindex) {
    if (!el) {
      el = document.createElement("meta");
      el.name = "robots";
      el.dataset.id = id;
      document.head.appendChild(el);
    }
    el.content = "noindex, nofollow";
  } else if (el) {
    el.remove();
  }
}

export default function PublicStorefront() {
  const { handle } = useParams();
  const [store, setStore] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const lower = (handle || "").toLowerCase();
    document.title = `${lower} — Storefront`;
    axios.get(`${BACKEND_URL}/api/public/store/${lower}`)
      .then((r) => setStore(r.data))
      .catch((e) => setErr(e?.response?.data || { error: "load_failed" }));
  }, [handle]);

  // I5 — noindex when the app is running against the TikTok sandbox.
  useEffect(() => {
    if (!store) return;
    ensureMetaRobots(store.tiktok_app_env === "sandbox");
    return () => ensureMetaRobots(false);
  }, [store]);

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6" data-testid="store-not-found">
        <div className="text-center">
          <ShoppingBag className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <h1 className="text-xl font-semibold">Store not found</h1>
          <p className="text-sm text-zinc-500 mt-1">The handle <span className="mono">@{handle}</span> doesn&apos;t exist or has been removed.</p>
        </div>
      </div>
    );
  }
  if (!store) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-500 flex items-center justify-center text-sm">Loading…</div>;
  }

  const heroGradient = HERO_COLORS[store.hero_color] || HERO_COLORS.emerald;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-storefront">
      {/* Hero — big gradient band with avatar + name + tagline + bio */}
      <header
        className={`bg-gradient-to-br ${heroGradient} relative overflow-hidden`}
        data-testid="storefront-hero"
      >
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-10 sm:pt-16 sm:pb-14">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
            {store.avatar_url ? (
              <img src={store.avatar_url} alt=""
                   className="h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover border-4 border-zinc-100/10 shadow-2xl"
                   data-testid="storefront-avatar" />
            ) : (
              <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-zinc-100/10 backdrop-blur-md flex items-center justify-center text-5xl font-bold border-4 border-zinc-100/10 shadow-2xl"
                   data-testid="storefront-avatar-placeholder">
                {(store.display_name || store.handle)[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl sm:text-4xl font-serif tracking-tight leading-tight"
                  data-testid="storefront-display-name">
                {store.display_name}
              </h1>
              {store.tagline && (
                <p className="text-sm sm:text-base text-zinc-200/90 mt-1 font-medium"
                   data-testid="storefront-tagline">
                  {store.tagline}
                </p>
              )}
              <div className="text-xs mono text-zinc-300/70 mt-2">@{store.handle}</div>
              {store.bio && (
                <p className="text-sm text-zinc-100/90 mt-3 max-w-xl whitespace-pre-wrap"
                   data-testid="storefront-bio">
                  {store.bio}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {store.products.length === 0 ? (
          <div className="text-center py-16 text-zinc-600 text-sm" data-testid="storefront-empty">
            No picks yet — check back soon!
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5"
               data-testid="storefront-products">
            {store.products.map((p) => (
              <a
                key={p.id}
                href={p.affiliate_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="group bg-zinc-900 hover:bg-zinc-800 transition border border-zinc-800 hover:border-zinc-700 rounded-xl overflow-hidden flex flex-col"
                data-testid={`storefront-product-${p.id}`}
              >
                {p.image_url ? (
                  <div className="aspect-square bg-zinc-800 overflow-hidden">
                    <img src={p.image_url} alt={p.title || ""}
                         className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-zinc-700" />
                  </div>
                )}
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="text-sm font-medium text-zinc-100 line-clamp-2 leading-snug">
                    {p.title || "Untitled"}
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-1">
                    {p.price ? (
                      <div className="text-xs mono text-emerald-400 font-semibold">
                        {p.currency || "$"}{p.price}
                      </div>
                    ) : <span />}
                    <span className="text-[10px] mono inline-flex items-center gap-0.5 text-zinc-400 group-hover:text-emerald-300">
                      Get it <ExternalLink className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <footer className="text-center text-[11px] text-zinc-500 mt-14 pt-6 border-t border-zinc-900 space-y-2"
                data-testid="storefront-footer">
          <p className="max-w-md mx-auto leading-relaxed">
            As an affiliate I may earn from qualifying purchases via links on this page. #ad #affiliate
          </p>
          <div className="flex justify-center gap-3 mono" data-testid="storefront-legal-links">
            <a href="/privacy" className="hover:text-zinc-300" data-testid="storefront-privacy-link">Privacy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-zinc-300" data-testid="storefront-terms-link">Terms</a>
            <span>·</span>
            <span className="text-zinc-700">Powered by AffiliatePilot</span>
          </div>
        </footer>
      </main>
    </div>
  );
}
